"use client";

import { Laptop, Moon, Sun } from "lucide-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { updateUserThemeModeAction } from "@/actions/user";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { parseUIMode } from "@/lib/utils";
import type { UIMode } from "@/types/user";

function handleThemeChange(theme: UIMode, setTheme: (theme: UIMode) => void) {
  const preferredTheme = parseUIMode(theme);
  updateUserThemeModeAction(theme);
  setTheme(preferredTheme);
}

export function ModeToggle({ userTheme }: { userTheme?: UIMode }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const t = useTranslations();
  const current = (mounted ? theme : "dark") as UIMode;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (userTheme) handleThemeChange(userTheme, setTheme);
  }, [userTheme, setTheme]);

  const TriggerIcon = () => {
    const modePriority = userTheme ?? current;
    if (!mounted) return <Sun className="size-5" />;
    if (modePriority === "dark") return <Moon className="size-5" />;
    if (modePriority === "system") return <Laptop className="size-5" />;
    return <Sun className="size-5" />;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="p-1 cursor-pointer">
          <TriggerIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleThemeChange("light", setTheme)}
          className="cursor-pointer"
        >
          <Sun className="mr-2 size-4" />
          {t("themeSwitch.light")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleThemeChange("dark", setTheme)}
          className="cursor-pointer"
        >
          <Moon className="mr-2 size-4" />
          {t("themeSwitch.dark")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleThemeChange("system", setTheme)}
          className="cursor-pointer"
        >
          <Laptop className="mr-2 size-4" />
          {t("themeSwitch.system")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ModeToggleSkeleton() {
  return <Button variant="ghost" size="icon" disabled className="h-10 w-10" />;
}

export function SimpleThemeToggle({ userTheme }: { userTheme?: UIMode }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (userTheme) {
      const preferredTheme = parseUIMode(userTheme);
      setTheme(preferredTheme);
    }
  }, [userTheme, setTheme]);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    updateUserThemeModeAction(newTheme as UIMode);
    setTheme(newTheme);
  };

  if (!mounted) {
    return (
      <Button variant="ghost" size="icon" disabled>
        <Sun className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      className="group cursor-pointer"
    >
      {theme === "dark" ? (
        <Moon className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-30 text-primary" />
      ) : (
        <Sun className="h-5 w-5 transition-transform duration-300 group-hover:rotate-180 text-warning" />
      )}
    </Button>
  );
}
